<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Careers</span>
        <h1 class="hris-page-title">Employment Application</h1>
        <p class="hris-page-subtitle">Complete the form below to apply for an open position.</p>
      </div>

      <q-form ref="newAppForm">
        <div class="hris-form-section text-center">
          <img class="logo q-mb-sm" src="../../assets/sgcc-black.png" alt="BeaPayDay">
        </div>

        <div v-if="selectedVacancy" class="hris-form-banner q-mb-md">
          <div class="row items-center justify-between q-col-gutter-sm">
            <div class="col">
              <div class="text-weight-bold">Applying for: {{ selectedVacancy.title }}</div>
              <div class="text-caption">
                {{ selectedVacancy.department || 'General' }} &bull; {{ selectedVacancy.site || 'Any site' }}
              </div>
            </div>
            <span class="hris-badge hris-badge-accent">{{ selectedVacancy.employment_type }}</span>
          </div>
        </div>

        <div class="full-width q-mb-md text-right">
          <b>Application Date</b>{{ dateToday }}
        </div>

        <div class="hris-form-section">
          <h3 class="hris-form-section-title">
            <q-icon name="fa fa-users" size="sm" class="q-mr-sm"></q-icon>
            Personal Information
          </h3>
          <div class="row col-12 q-col-gutter-md">
            <div class="col-4">
              <q-input v-model="form.firstname" @input="val => {form.firstname = val.toUpperCase()}" label="First Name" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-input>
            </div>
            <div class="col-4">
              <q-input v-model="form.middlename" @input="val => {form.middlename = val.toUpperCase()}" label="Middle Name" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-input>
            </div>
            <div class="col-4">
              <q-input v-model="form.lastname" @input="val => {form.lastname = val.toUpperCase()}" label="Last Name" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-input>
            </div>
          </div>
          <div class="row col-12 q-col-gutter-md">
            <div class="col-6">
              <q-input v-model="form.present_address" @input="val => {form.present_address = val.toUpperCase()}" label="Present Address" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-input>
            </div>
            <div class="col-3">
              <q-select v-model="form.present_province" :options="provinces" @input="setProvince('Present',form.present_province)" label="Province" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-select>
            </div>
            <div class="col-3">
              <q-select v-model="form.present_mun_city" :options="present_cities_municipals" label="City / Municipality" outlined dense color="green" lazy-rules :rules="[formRules.required]">
                          <template v-slot:no-option>
                            <q-item>
                              <q-item-section class="text-italic text-grey">
                                Select province first
                              </q-item-section>
                            </q-item>
                          </template>
              </q-select>
            </div>
          </div>
          <div class="row col-12 q-col-gutter-md">
            <div class="col-6">
              <q-input v-model="form.permanent_address" @input="val => {form.permanent_address = val.toUpperCase()}" label="Permament Address" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-input>
            </div>
            <div class="col-3">
              <q-select v-model="form.permanent_province" :options="provinces" @input="setProvince('Permament',form.permanent_province)" label="Province" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-select>
            </div>
            <div class="col-3">
              <q-select v-model="form.permanent_mun_city" :options="permanent_cities_municipals" label="City / Municipality" outlined dense color="green" lazy-rules :rules="[formRules.required]">
                          <template v-slot:no-option>
                            <q-item>
                              <q-item-section class="text-italic text-grey">
                                Select province first
                              </q-item-section>
                            </q-item>
                          </template>
              </q-select>
            </div>
          </div>
          <div class="row col-12 q-col-gutter-md">
            <div class="col-4">
              <q-input v-model="form.email" @input="val => {form.email = val.toUpperCase()}" label="Email Address" outlined dense color="green" lazy-rules :rules="[formRules.email]"></q-input>
            </div>
            <div class="col-4">
              <q-input v-model="form.mobile_number" label="Mobile Number" outlined dense color="green" lazy-rules :rules="[formRules.numberRequired]"></q-input>
            </div>
            <div class="col-4">
              <q-input v-model="form.tel_number" label="Telephone Number" outlined dense color="green"></q-input>
            </div>
          </div>
          <div class="row col-12 q-col-gutter-md">
            <div class="col-3">
              <q-select v-model="form.gender" :options="['MALE','FEMALE']" label="Gender" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-select>
            </div>
            <div class="col-3">
              <q-input v-model="form.religion" @input="val => {form.religion = val.toUpperCase()}" label="Religion" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-input>
            </div>
            <div class="col-3">
              <q-select v-model="form.civil_status" :options="['SINGLE','MARRIED','WIDOWED']" label="Civil Status" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-select>
            </div>
            <div class="col-3">
              <q-input v-model="birthdateInput" label="Birthday" outlined dense color="green" readonly>
                <template v-slot:append>
                  <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                    <q-popup-proxy ref="qbirthDate" transition-show="flip-down" transition-hide="flip-up">
                      <q-date v-model="form.birthdate" @input="$refs.qbirthDate.hide()" today-btn color="green" minimal></q-date>
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
          </div>
        </div>

        <div class="hris-form-section">
          <h3 class="hris-form-section-title">
            <q-icon name="fa fa-search-location" size="sm" class="q-mr-sm"></q-icon>
            How Did You Know This Company?
          </h3>
          <div class="row col-12 q-col-gutter-md">
            <div class="col-3">
              <q-select v-model="form.through" :options="['JOBFAIR','POSTING','REFERRAL','RE-APPLY','WALK-IN','OTHERS']" @input="knowThrough(form.through)" label="Through" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-select>
            </div>
            <div class="col-3" v-if="dFalse.field_subthrough_posting">
              <q-select v-model="form.through_suboption" :options="['FACEBOOK - NEWS FEED','FACEBOOK - STERLING PAGE','INDEED','JOBSTREET','LINKEDIN']" :label="field_subthrough_title" outlined dense color="green" :disable="dTrue.sub1_disabled"></q-select>
            </div>
            <div class="col-3" v-if="dFalse.field_subthrough_referral">
              <q-input v-model="form.through_suboption" @input="val => {form.through_suboption = val.toUpperCase()}" :label="field_subthrough_title" outlined dense color="green" :disable="dTrue.sub2_disabled"></q-input>
            </div>
            <div class="col-3" v-if="dTrue.field_subthrough_others">
              <q-input v-model="form.through_suboption" @input="val => {form.through_suboption = val.toUpperCase()}" :label="field_subthrough_title" outlined dense color="green" :filled="dTrue.subthrough_input_style" :disable="dTrue.sub3_disabled"></q-input>
            </div>
            <div class="col-3">
              <q-select v-model="form.position_applying_for" :options="['CALL CENTER AGENT','TEAM LEADER','ASSISTANT TEAM LEADER','OTHERS']" @input="otherPosition(form.position_applying_for)" label="Position Applying For" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-select>
            </div>
            <div class="col-3">
              <q-input v-model="form.other_position" @input="val => {form.other_position = val.toUpperCase()}" label="Others (Please Specify)" outlined dense color="green" :filled="dTrue.other_position_input_style" :disable="dTrue.other_pos_disabled"></q-input>
            </div>
          </div>
        </div>

        <div class="hris-form-section">
          <h3 class="hris-form-section-title">
            <q-icon name="fa fa-id-badge" size="sm" class="q-mr-sm"></q-icon>
            Work Experiences
          </h3>
          <div class="text-subtitle2 q-mb-md">Call Center Experience</div>
          <div class="row col-12 q-col-gutter-md">
            <div class="col-3">
              <q-select v-model="form.call_center_exp" :options="['YES','NO']" @input="withCCExp(form.call_center_exp)" label="With Call Center Experience?" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-select>
            </div>
            <div class="col-3">
              <q-input v-model="form.call_center_exp_position" @input="val => {form.call_center_exp_position = val.toUpperCase()}" label="Position" outlined dense color="green" :filled="dTrue.ccexp_style" :disable="dTrue.ccexp_disabled"></q-input>
            </div>
            <div class="col-3">
              <q-select v-model="form.call_center_exp_years" :options="expYears" label="Year(s) of Experience" outlined dense color="green" :filled="dTrue.ccexp_style" :disable="dTrue.ccexp_disabled"></q-select>
            </div>
            <div class="col-3">
              <q-select v-model="form.call_center_exp_months" :options="expMonths" label="Month(s) of Experience" outlined dense color="green" :filled="dTrue.ccexp_style" :disable="dTrue.ccexp_disabled"></q-select>
            </div>
          </div>
          <div class="row col-12 q-col-gutter-md" v-if="dFalse.withCCExpComp">
            <div class="col-3">
              <q-input v-model="form.cc_exp_company_name" @input="val => {form.cc_exp_company_name = val.toUpperCase()}" label="Company Name" outlined dense color="green"></q-input>
            </div>
            <div class="col-3">
              <q-input v-model="form.cc_exp_company_address" @input="val => {form.cc_exp_company_address = val.toUpperCase()}" label="Company Address" outlined dense color="green"></q-input>
            </div>
            <div class="col-3">
              <q-input v-model="form.cc_exp_account_handled" @input="val => {form.cc_exp_account_handled = val.toUpperCase()}" label="Account Handled" outlined dense color="green"></q-input>
            </div>
            <div class="col-3">
              <q-input v-model="form.cc_exp_reason_leaving" @input="val => {form.cc_exp_reason_leaving = val.toUpperCase()}" label="Reason for Leaving" outlined dense color="green"></q-input>
            </div>
          </div>
          <div class="text-subtitle2 q-mt-md q-mb-md">Other Work Experience</div>
          <div class="row col-12 q-col-gutter-md">
            <div class="col-3">
              <q-select v-model="form.other_exp" :options="['YES','NO']" @input="withOtherExp(form.other_exp)" label="With Other Work Experience?" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-select>
            </div>
            <div class="col-3">
              <q-input v-model="form.other_exp_position" @input="val => {form.other_exp_position = val.toUpperCase()}" label="Position" outlined dense color="green" :filled="dTrue.other_exp_style" :disable="dTrue.other_exp_disabled"></q-input>
            </div>
            <div class="col-3">
              <q-select v-model="form.other_exp_years" :options="expYears" label="Year(s) of Experience" outlined dense color="green" :filled="dTrue.other_exp_style" :disable="dTrue.other_exp_disabled"></q-select>
            </div>
            <div class="col-3">
              <q-select v-model="form.other_exp_months" :options="expMonths" label="Month(s) of Experience" outlined dense color="green" :filled="dTrue.other_exp_style" :disable="dTrue.other_exp_disabled"></q-select>
            </div>
          </div>
          <div class="row col-12 q-mb-md q-col-gutter-md" v-if="dFalse.withOtherExpComp">
            <div class="col-3">
              <q-input v-model="form.other_exp_company_name" @input="val => {form.other_exp_company_name = val.toUpperCase()}" label="Company Name" outlined dense color="green"></q-input>
            </div>
            <div class="col-3">
              <q-input v-model="form.other_exp_company_address" @input="val => {form.other_exp_company_address = val.toUpperCase()}" label="Company Address" outlined dense color="green"></q-input>
            </div>
            <div class="col-3">
              <q-input v-model="form.other_exp_nature_business" @input="val => {form.other_exp_nature_business = val.toUpperCase()}" label="Nature of Business" outlined dense color="green"></q-input>
            </div>
            <div class="col-3">
              <q-input v-model="form.other_exp_reason_leaving" @input="val => {form.other_exp_reason_leaving = val.toUpperCase()}" label="Reason for Leaving" outlined dense color="green"></q-input>
            </div>
          </div>
        </div>

        <div class="hris-form-section">
          <h3 class="hris-form-section-title">
            <q-icon name="fa fa-user-graduate" size="sm" class="q-mr-sm"></q-icon>
            Educational Background
          </h3>
          <div class="row col-12 q-col-gutter-md">
            <div class="col-3">
              <q-select v-model="form.educ_background" :options="['HIGH SCHOOL GRADUATE','VOCATIONAL COURSE','COLLEGE LEVEL','DEGREE HOLDER']" @input="educAttained(form.educ_background)" label="Highest Educational Attainment" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-select>
            </div>
            <div class="col-1">
              <q-input v-model="form.year_attended" mask="####" label="Year Attended" outlined dense color="green" lazy-rules :rules="[formRules.numberRequired]" ref="educ_year_input"></q-input>
            </div>
            <div class="col-3">
              <q-input v-model="form.suc" @input="val => {form.suc = val.toUpperCase()}" label="School / University / College" outlined dense color="green" lazy-rules :rules="[formRules.required]" ref="educ_suc_input"></q-input>
            </div>
            <div class="col-3">
              <q-input v-model="form.course" @input="val => {form.course = val.toUpperCase()}" label="Course" placeholder="e.g. BS Information Technology" outlined dense color="green" :filled="dTrue.educ_bg_course_style" :disable="dTrue.educ_bg_course_disabled"></q-input>
            </div>
            <div class="col-2">
              <q-select v-model="form.year_level" :options="['1ST YEAR','2ND YEAR','3RD YEAR','4TH YEAR','5TH YEAR']" label="Year Level" outlined dense color="green" :filled="dTrue.educ_bg_yearlevel_style" :disable="dTrue.educ_bg_yearlevel_disabled"></q-select>
            </div>
          </div>
        </div>

        <div class="hris-form-section">
          <h3 class="hris-form-section-title">
            <q-icon name="fa fa-hospital" size="sm" class="q-mr-sm"></q-icon>
            Medical / Health Background
          </h3>
          <div class="row col-12 q-col-gutter-md">
            <span>List down ilness(es) you suffered during the last 3 years, which incapacitated or grounded you at home or hospital for more than a week.</span>
          </div>
          <div class="row col-12 q-mt-md q-mb-sm q-col-gutter-md">
            <div class="col-6 text-center"><b>NATURE OF ILLNESS</b></div>
            <div class="col-6 text-center"><b>APPROXIMATE DATES (e.g. January 2019 - April 2019)</b></div>
          </div>
          <div class="row col-12 q-mb-sm q-col-gutter-sm">
            <div class="row col-6">
              <q-checkbox v-model="illness1" @input="illnessCheck(illness1,'illness1')" true-value="yes" false-value="no" color="green"></q-checkbox>
              <div class="col-11">
                <q-input outlined dense color="green" v-model="form.nature_of_illness1" @input="val => {form.nature_of_illness1 = val.toUpperCase()}" :filled="dTrue.illness1_style" :disable="dTrue.illness1_disabled"></q-input>
              </div>
            </div>
            <div class="row col-6 q-col-gutter-xs">
              <div class="col-3">
                <q-select outlined dense color="green" v-model="form.approx_from_month1" :options="months" :filled="dTrue.illness1_style" :disable="dTrue.illness1_disabled"></q-select>
              </div>
              <div class="col-2">
                <q-input outlined dense color="green" v-model="form.approx_from_year1" mask="####" :filled="dTrue.illness1_style" :disable="dTrue.illness1_disabled"></q-input>
              </div>
              <div class="row col-2 justify-center items-center">
                <span>To</span>
              </div>
              <div class="col-3">
                <q-select outlined dense color="green" v-model="form.approx_to_month1" :options="months" :filled="dTrue.illness1_style" :disable="dTrue.illness1_disabled"></q-select>
              </div>
              <div class="col-2">
                <q-input outlined dense color="green" v-model="form.approx_to_year1" mask="####" :filled="dTrue.illness1_style" :disable="dTrue.illness1_disabled"></q-input>
              </div>
            </div>
          </div>
          <div class="row col-12 q-mb-sm q-col-gutter-sm">
            <div class="row col-6">
              <q-checkbox v-model="illness2" @input="illnessCheck(illness2,'illness2')" true-value="yes" false-value="no" color="green"></q-checkbox>
              <div class="col-11">
                <q-input outlined dense color="green" v-model="form.nature_of_illness2" @input="val => {form.nature_of_illness2 = val.toUpperCase()}" :filled="dTrue.illness2_style" :disable="dTrue.illness2_disabled"></q-input>
              </div>
            </div>
            <div class="row col-6 q-col-gutter-xs">
              <div class="col-3">
                <q-select outlined dense color="green" v-model="form.approx_from_month2" :options="months" :filled="dTrue.illness2_style" :disable="dTrue.illness2_disabled"></q-select>
              </div>
              <div class="col-2">
                <q-input outlined dense color="green" v-model="form.approx_from_year2" mask="####" :filled="dTrue.illness2_style" :disable="dTrue.illness2_disabled"></q-input>
              </div>
              <div class="row col-2 justify-center items-center">
                <span>To</span>
              </div>
              <div class="col-3">
                <q-select outlined dense color="green" v-model="form.approx_to_month2" :options="months" :filled="dTrue.illness2_style" :disable="dTrue.illness2_disabled"></q-select>
              </div>
              <div class="col-2">
                <q-input outlined dense color="green" v-model="form.approx_to_year2" mask="####" :filled="dTrue.illness2_style" :disable="dTrue.illness2_disabled"></q-input>
              </div>
            </div>
          </div>
          <div class="row col-12 q-mb-sm q-col-gutter-sm">
            <div class="row col-6">
              <q-checkbox v-model="illness3" @input="illnessCheck(illness3,'illness3')" true-value="yes" false-value="no" color="green"></q-checkbox>
              <div class="col-11">
                <q-input outlined dense color="green" v-model="form.nature_of_illness3" @input="val => {form.nature_of_illness3 = val.toUpperCase()}" :filled="dTrue.illness3_style" :disable="dTrue.illness3_disabled"></q-input>
              </div>
            </div>
            <div class="row col-6 q-col-gutter-xs">
              <div class="col-3">
                <q-select outlined dense color="green" v-model="form.approx_from_month3" :options="months" :filled="dTrue.illness3_style" :disable="dTrue.illness3_disabled"></q-select>
              </div>
              <div class="col-2">
                <q-input outlined dense color="green" v-model="form.approx_from_year3" mask="####" :filled="dTrue.illness3_style" :disable="dTrue.illness3_disabled"></q-input>
              </div>
              <div class="row col-2 justify-center items-center">
                <span>To</span>
              </div>
              <div class="col-3">
                <q-select outlined dense color="green" v-model="form.approx_to_month3" :options="months" :filled="dTrue.illness3_style" :disable="dTrue.illness3_disabled"></q-select>
              </div>
              <div class="col-2">
                <q-input outlined dense color="green" v-model="form.approx_to_year3" mask="####" :filled="dTrue.illness3_style" :disable="dTrue.illness3_disabled"></q-input>
              </div>
            </div>
          </div>
        </div>

        <div class="hris-form-section">
          <h3 class="hris-form-section-title">
            <q-icon name="fa fa-phone-volume" size="sm" class="q-mr-sm"></q-icon>
            Person to Notify in Case of Emergency
          </h3>
          <div class="row col-12 q-col-gutter-md">
            <div class="col-4">
              <q-input v-model="form.emergency_name" @input="val => {form.emergency_name = val.toUpperCase()}" label="Contact Person" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-input>
            </div>
            <div class="col-4">
              <q-input v-model="form.emergency_relation" @input="val => {form.emergency_relation = val.toUpperCase()}" label="Relationship" outlined dense color="green" lazy-rules :rules="[formRules.required]"></q-input>
            </div>
            <div class="col-4">
              <q-input v-model="form.emergency_contact" label="Contact Number" outlined dense color="green" lazy-rules :rules="[formRules.numberRequired]"></q-input>
            </div>
          </div>
        </div>

        <div class="hris-form-actions justify-center">
          <q-btn @click="resetForm" label="Reset" unelevated class="hris-q-outline" style="min-width: 160px"></q-btn>
          <q-btn @click="sendApplication" :loading="appSendingEffect" label="Submit Application" unelevated class="hris-q-primary" style="min-width: 260px">
            <template v-slot:loading>
              <q-spinner-dots class="on-left"></q-spinner-dots>
              Submitting Application
            </template>
          </q-btn>
        </div>
      </q-form>

      <q-dialog v-model="spinner" persistent>
        <div class="hris-loading" style="background: var(--hris-surface); border-radius: var(--hris-radius); padding: 32px 44px;">
          <q-spinner color="green" size="40px"></q-spinner>
        </div>
      </q-dialog>

      <q-dialog v-model="viewTrackingID" persistent transition-show="scale" transition-hide="scale">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">Your Tracking ID</h2>
          </div>
          <div class="hris-modal-body text-center">
            <p class="tracking-id-text">Please save this tracking ID as it will serve as your ID in your next appointment.</p>
            <div class="text-h5 text-weight-bold q-my-md">{{ trackingID }}</div>
          </div>
          <div class="hris-modal-footer justify-center">
            <q-btn label="OK" unelevated class="hris-q-primary" v-close-popup></q-btn>
          </div>
        </div>
      </q-dialog>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import vueCookies from 'vue-cookies';
import { date } from 'quasar';

let today = Date.now();
let dateToday = ':  ' + date.formatDate(today, ' dddd,  MMMM DD, YYYY');

const scrollToTop = () => {
  const c = document.documentElement.scrollTop || document.body.scrollTop;
  if (c > 0) {
    window.requestAnimationFrame(scrollToTop);
    window.scrollTo(0, c - c / 8);
  }
};

const expYears = []
for (let i = 0; i <= 30; i++) {
  if (i < 2) {
    expYears.push(i + ' YEAR')
  } else {
    expYears.push(i + ' YEARS')
  }
}

const expMonths = []
for (let i = 0; i <= 11; i++) {
  if (i < 2) {
    expMonths.push(i + ' MONTH')
  } else {
    expMonths.push(i + ' MONTHS')
  }
}

const months = Array.from({ length: 12 }, (e, i) => {
  return new Date(null, i + 1, null).toLocaleDateString("en", { month: "long" }).toUpperCase();
})

export default {
  name: "ApplicationEmploymentNA",
  data: () => ({
    dateToday,
    expYears,
    expMonths,
    months,
    spinner: false,
    appSendingEffect: false,
    provinces: [],
    present_cities_municipals: [],
    permanent_cities_municipals: [],
    field_subthrough_title: 'Posting / Referrals / Others',
    dTrue: {
      field_subthrough_others: true,
      sub1_disabled: true,
      sub2_disabled: true,
      sub3_disabled: true,
      other_pos_disabled: true,
      subthrough_input_style: true,
      other_position_input_style: true,
      ccexp_style: true,
      ccexp_disabled: true,
      other_exp_style: true,
      other_exp_disabled: true,
      educ_bg_course_style: true,
      educ_bg_yearlevel_style: true,
      educ_bg_course_disabled: true,
      educ_bg_yearlevel_disabled: true,
      illness1_style: true,
      illness2_style: true,
      illness3_style: true,
      illness1_disabled: true,
      illness2_disabled: true,
      illness3_disabled: true,
    },
    dFalse: {
      field_subthrough_posting: false,
      field_subthrough_referral: false,
      withCCExpComp: false,
      withOtherExpComp: false,
    },
    field_subthrough_selected: null,
    other_pos_selected: null,
    illness1: 'no',
    illness2: 'no',
    illness3: 'no',
    form: {
      firstname: null,
      middlename: null,
      lastname: null,
      present_address: null,
      present_province: null,
      present_mun_city: null,
      permanent_address: null,
      permanent_province: null,
      permanent_mun_city: null,
      email: null,
      mobile_number: null,
      tel_number: null,
      gender: null,
      religion: null,
      civil_status: null,
      birthdate: null,
      job_vacancy_id: null,
      through: null,
      through_suboption: null,
      position_applying_for: null,
      other_position: null,
      call_center_exp: null,
      call_center_exp_position: null,
      call_center_exp_years: null,
      call_center_exp_months: null,
      cc_exp_company_name: null,
      cc_exp_company_address: null,
      cc_exp_account_handled: null,
      cc_exp_reason_leaving: null,
      other_exp: null,
      other_exp_position: null,
      other_exp_years: null,
      other_exp_months: null,
      other_exp_company_name: null,
      other_exp_company_address: null,
      other_exp_nature_business: null,
      other_exp_reason_leaving: null,
      educ_background: null,
      year_attended: null,
      suc: null,
      course: null,
      year_level: null,
      nature_of_illness1: null,
      approx_from_month1: null,
      approx_from_year1: null,
      approx_to_month1: null,
      approx_to_year1: null,
      nature_of_illness2: null,
      approx_from_month2: null,
      approx_from_year2: null,
      approx_to_month2: null,
      approx_to_year2: null,
      nature_of_illness3: null,
      approx_from_month3: null,
      approx_from_year3: null,
      approx_to_month3: null,
      approx_to_year3: null,
      emergency_name: null,
      emergency_relation: null,
      emergency_contact: null
    },
    selectedVacancy: null,
    selectedVacancyLoading: false,
    trackingID: null,
    viewTrackingID: false
  }),
  methods: {
    loadSelectedVacancy: function() {
      const vacancyId = this.$route.query.vacancy_id;
      if (!vacancyId) {
        return;
      }

      this.selectedVacancyLoading = true;
      axios.get(`${this.apiUrl}/recruitments/vacancies/public/${vacancyId}`)
        .then((res) => {
          this.selectedVacancy = res.data.data || null;
          this.form.job_vacancy_id = this.selectedVacancy ? this.selectedVacancy.id : null;
          if (this.selectedVacancy) {
            this.form.position_applying_for = 'OTHERS';
            this.otherPosition('OTHERS');
            this.form.other_position = this.selectedVacancy.title;
          }
        })
        .catch(() => {
          this.selectedVacancy = null;
          this.form.job_vacancy_id = null;
        })
        .finally(() => {
          this.selectedVacancyLoading = false;
        });
    },
    loadProvinces: function() {
      axios.get(this.apiUrl + '/recruitments/retrieve-provinces-list')
        .then((res) => {
          this.provinces = [];
          var provs = res.data.data;
          for (let prov of Object.values(provs)) {
            this.provinces.push(prov.province.toUpperCase());
          }
          Object.freeze(this.provinces);
        });
    },

    setProvince: function(provType, selectedProvince) {
      axios.get(this.apiUrl + '/recruitments/retrieve-cities-municipals-list', {
          params: {
            id: selectedProvince
          }
        })
        .then((res) => {
          this.present_cities_municipals = [];
          this.permanent_cities_municipals = [];
          var cities = res.data.data;
          if (provType == "Present") {
            for (let city of Object.values(cities)) {
              this.present_cities_municipals.push(city.city_municipality.toUpperCase());
            }
            Object.freeze(this.present_cities_municipals);
          } else {
            for (let city of Object.values(cities)) {
              this.permanent_cities_municipals.push(city.city_municipality.toUpperCase());
            }
            Object.freeze(this.permanent_cities_municipals);
          }
        });
    },

    knowThrough: function(knowThrough) {
      switch (knowThrough) {
        case 'JOBFAIR':
          this.field_subthrough_title = 'Posting / Referrals / Others';
          this.dFalse.field_subthrough_posting = false;
          this.dTrue.sub1_disabled = true;
          this.dFalse.field_subthrough_referral = false;
          this.dTrue.sub2_disabled = true;
          this.dTrue.field_subthrough_others = true;
          this.dTrue.sub3_disabled = true;
          this.form.through_suboption = null;
          this.field_subthrough_selected = knowThrough;
          this.dTrue.subthrough_input_style = true;
          break;
        case 'POSTING':
          this.field_subthrough_title = 'Posting';
          this.dFalse.field_subthrough_posting = true;
          this.dTrue.sub1_disabled = false;
          this.dFalse.field_subthrough_referral = false;
          this.dTrue.sub2_disabled = true;
          this.dTrue.field_subthrough_others = false;
          this.dTrue.sub3_disabled = true;
          this.form.through_suboption = null;
          this.dTrue.subthrough_input_style = false;
          break;
        case 'REFERRAL':
          this.field_subthrough_title = 'Referred By';
          this.dFalse.field_subthrough_posting = false;
          this.dTrue.sub1_disabled = true;
          this.dFalse.field_subthrough_referral = true;
          this.dTrue.sub2_disabled = false;
          this.dTrue.field_subthrough_others = false;
          this.dTrue.sub3_disabled = true;
          this.form.through_suboption = null;
          this.dTrue.subthrough_input_style = false;
          break;
        case 'RE-APPLY':
          this.field_subthrough_title = 'Posting / Referrals / Others';
          this.dFalse.field_subthrough_posting = false;
          this.dTrue.sub1_disabled = true;
          this.dFalse.field_subthrough_referral = false;
          this.dTrue.sub2_disabled = true;
          this.dTrue.field_subthrough_others = true;
          this.dTrue.sub3_disabled = true;
          this.form.through_suboption = null;
          this.field_subthrough_selected = knowThrough;
          this.dTrue.subthrough_input_style = true;
          break;
        case 'WALK-IN':
          this.field_subthrough_title = 'Posting / Referrals / Others';
          this.dFalse.field_subthrough_posting = false;
          this.dTrue.sub1_disabled = true;
          this.dFalse.field_subthrough_referral = false;
          this.dTrue.sub2_disabled = true;
          this.dTrue.field_subthrough_others = true;
          this.dTrue.sub3_disabled = true;
          this.form.through_suboption = null;
          this.field_subthrough_selected = knowThrough;
          this.dTrue.subthrough_input_style = true;
          break;
        case 'OTHERS':
          this.field_subthrough_title = 'Others (Please Specify)';
          this.dFalse.field_subthrough_posting = false;
          this.dTrue.sub1_disabled = true;
          this.dFalse.field_subthrough_referral = false;
          this.dTrue.sub2_disabled = true;
          this.dTrue.field_subthrough_others = true;
          this.dTrue.sub3_disabled = false;
          this.form.through_suboption = null;
          this.dTrue.subthrough_input_style = false;
          break;
      }
    },

    otherPosition: function(otherPosition) {
      if (otherPosition == 'OTHERS') {
        this.dTrue.other_pos_disabled = false;
        this.dTrue.other_position_input_style = false;
      } else {
        this.dTrue.other_pos_disabled = true;
        this.other_pos_selected = otherPosition;
        this.dTrue.other_position_input_style = true;
        this.form.other_position = null;
      }
    },

    withCCExp: function(withCCExp) {
      if (withCCExp == 'YES') {
        this.dTrue.ccexp_style = false;
        this.dTrue.ccexp_disabled = false;
        this.dFalse.withCCExpComp = true;
      } else {
        this.dTrue.ccexp_style = true;
        this.dTrue.ccexp_disabled = true;
        this.dFalse.withCCExpComp = false;
        this.form.call_center_exp_position = null;
        this.form.call_center_exp_years = null;
        this.form.call_center_exp_months = null;
        this.form.cc_exp_company_name = null;
        this.form.cc_exp_company_address = null;
        this.form.cc_exp_account_handled = null;
        this.form.cc_exp_reason_leaving = null;
      }
    },

    withOtherExp: function(withOtherExp) {
      if (withOtherExp == 'YES') {
        this.dTrue.other_exp_style = false;
        this.dTrue.other_exp_disabled = false;
        this.dFalse.withOtherExpComp = true;
      } else {
        this.dTrue.other_exp_style = true;
        this.dTrue.other_exp_disabled = true;
        this.dFalse.withOtherExpComp = false;
        this.form.other_exp_position = null;
        this.form.other_exp_years = null;
        this.form.other_exp_months = null;
        this.form.other_exp_company_name = null;
        this.form.other_exp_company_address = null;
        this.form.other_exp_nature_business = null;
        this.form.other_exp_reason_leaving = null;
      }
    },

    educAttained: function(educAttained) {
      if (educAttained == 'HIGH SCHOOL GRADUATE') {
        this.dTrue.educ_bg_course_style = true;
        this.dTrue.educ_bg_yearlevel_style = true;
        this.dTrue.educ_bg_course_disabled = true;
        this.dTrue.educ_bg_yearlevel_disabled = true;
        this.form.year_attended = null;
        this.form.suc = null;
        this.form.course = null;
        this.form.year_level = null;
        this.$refs.educ_year_input.resetValidation();
        this.$refs.educ_suc_input.resetValidation();
      } else if (educAttained == 'VOCATIONAL COURSE') {
        this.dTrue.educ_bg_course_style = false;
        this.dTrue.educ_bg_yearlevel_style = true;
        this.dTrue.educ_bg_course_disabled = false;
        this.dTrue.educ_bg_yearlevel_disabled = true;
        this.form.year_attended = null;
        this.form.suc = null;
        this.form.course = null;
        this.form.year_level = null;
        this.$refs.educ_year_input.resetValidation();
        this.$refs.educ_suc_input.resetValidation();
      } else if (educAttained == 'COLLEGE LEVEL') {
        this.dTrue.educ_bg_course_style = false;
        this.dTrue.educ_bg_yearlevel_style = false;
        this.dTrue.educ_bg_course_disabled = false;
        this.dTrue.educ_bg_yearlevel_disabled = false;
        this.form.year_attended = null;
        this.form.suc = null;
        this.form.course = null;
        this.form.year_level = null;
        this.$refs.educ_year_input.resetValidation();
        this.$refs.educ_suc_input.resetValidation();
      } else {
        this.dTrue.educ_bg_course_style = false;
        this.dTrue.educ_bg_yearlevel_style = true;
        this.dTrue.educ_bg_course_disabled = false;
        this.dTrue.educ_bg_yearlevel_disabled = true;
        this.form.year_attended = null;
        this.form.suc = null;
        this.form.course = null;
        this.form.year_level = null;
        this.$refs.educ_year_input.resetValidation();
        this.$refs.educ_suc_input.resetValidation();
      }
    },

    illnessCheck: function(illnessField, illnessOrder) {
      if (illnessOrder == 'illness1') {
        if (illnessField == 'yes') {
          this.dTrue.illness1_style = false;
          this.dTrue.illness1_disabled = false;
        } else {
          this.dTrue.illness1_style = true;
          this.dTrue.illness1_disabled = true;
          this.form.nature_of_illness1 = null;
          this.form.approx_from_month1 = null;
          this.form.approx_from_year1 = null;
          this.form.approx_to_month1 = null;
          this.form.approx_to_year1 = null;
        }
      } else if (illnessOrder == 'illness2') {
        if (illnessField == 'yes') {
          this.dTrue.illness2_style = false;
          this.dTrue.illness2_disabled = false;
        } else {
          this.dTrue.illness2_style = true;
          this.dTrue.illness2_disabled = true;
          this.form.nature_of_illness2 = null;
          this.form.approx_from_month2 = null;
          this.form.approx_from_year2 = null;
          this.form.approx_to_month2 = null;
          this.form.approx_to_year2 = null;
        }
      } else {
        if (illnessField == 'yes') {
          this.dTrue.illness3_style = false;
          this.dTrue.illness3_disabled = false;
        } else {
          this.dTrue.illness3_style = true;
          this.dTrue.illness3_disabled = true;
          this.form.nature_of_illness3 = null;
          this.form.approx_from_month3 = null;
          this.form.approx_from_year3 = null;
          this.form.approx_to_month3 = null;
          this.form.approx_to_year3 = null;
        }
      }
    },

    sendApplication: function() {
      this.$refs.newAppForm.validate().then(success => {
        if (success) {
          this.appSendingEffect = true;
          this.form.birthdate = date.formatDate(this.form.birthdate, 'YYYY-MM-DD');
          let formData = new FormData();
          formData.append('form_data', JSON.stringify(this.form));

          axios.post(this.apiUrl + "/recruitments/application/submit", formData)
            .then((res) => {
              this.trackingID = res.data && res.data.data ? res.data.data : res.data;
              setTimeout(() => {
                this.appSendingEffect = false;
                this.$q.notify({
                  color: 'indigo-9',
                  textColor: 'white',
                  icon: 'fa fa-envelope',
                  message: 'Application Submitted'
                });
                this.resetForm();
                scrollToTop();
                this.viewTrackingID = true;
              }, 2000);
            })
            .catch(() => {
              this.appSendingEffect = false;
              this.$q.notify({
                color: 'negative',
                message: 'Failed to submit application. Please check your information and try again.'
              });
            });
        }
      });
    },

    resetForm: function() {
      var self = this;
      Object.keys(this.form).forEach(function(key, index) {
        self.form[key] = null;
      });
      Object.keys(this.dTrue).forEach(function(key, index) {
        self.dTrue[key] = true;
      });
      Object.keys(this.dFalse).forEach(function(key, index) {
        self.dFalse[key] = false;
      });
      this.field_subthrough_title = 'Posting / Referrals / Others';
      this.field_subthrough_selected = null;
      this.other_pos_selected = null;
      this.illness1 = 'no';
      this.illness2 = 'no';
      this.illness3 = 'no';
      this.$refs.newAppForm.resetValidation();
      setTimeout(() => {
        scrollToTop();
      }, 200);
    }
  },
  computed: {
    birthdateInput: function() {
      if (this.form.birthdate == null) {
        return 'Please select date of birth...';
      } else {
        let d = new Date(this.form.birthdate);
        return date.formatDate(d, 'MMMM DD, YYYY').toUpperCase();
      }
    }
  },
  mounted() {
    this.loadProvinces();
    this.loadSelectedVacancy();
  }
};
</script>
<style scoped>
/* .login-box .title {} */

.form-box {
  border-top: 1px dashed #ddd;
  padding-top: 15px;
  margin-top: 50px;
}

.logo {
  width: 280px;
  max-width: 100%;
}

.text-h6 {
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: -moz-none;
  -o-user-select: none;
  user-select: none;
}

.tracking-id-text {
  font-size: 12px;
  margin-top: 12px;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: -moz-none;
  -o-user-select: none;
  user-select: none;
}
</style>
